/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals XLSX, saveAs, Blob, importScripts, postMessage */

import $ from './Util';

/**
 * Represents the MS-Excel data exporter class.
 */

export default class Excel {

    /** Column types. */
    static get COLUMN() { return { 
        STRING:     'string',
        NUMBER:     'number',
        DATE:       'date',
        DATETIME:   'datetime'
    };}

    /** Commands between the main thread and the Worker. */
    static get _COMMAND() { return { 
        PERCENT:    'percent',
        CREATED:    'created' 
    };}

    /** Default cell width. */
    static get DEFAULT_CELLWIDTH() { return 32; }
    
    /**
     * Creates a new NVA.Excel object and sets its attributes.
     * 
     * @param   {Object}    config      export configuration
     * @param   {Array}     data        export data
     * @param   {Function}  refresh     callback refresher function
     */

    constructor() {

        // Setting MS-Excel export configuration if it is specified

        if (arguments.length >= 1) {
            this.config = arguments[0];
        }

        // Setting export data if it is specified

        if (arguments.length >= 2) {
            this.data = arguments[1];
        }

        // Setting callback refresher function if it is specified

        if (arguments.length >= 3) {
            this.refresh = arguments[2];
        }
    }

    /**
     * Getter function of the 'config' property.
     * 
     * @return  {Object}    config      export configuration
     */

    get config() {
        return this._config;
    }

    /**
     * Setter function of the 'config' property.
     * 
     * @param   {Object}    value       export configuration
     */

    set config(value) {

        // Throwing error if the config is not an object

        if ($.isEmpty(value) || !$.isObject(value)) {
            throw new Error('config is mandatory and must be an object');
        }

        // Checking worker setting

        if ($.isEmpty(value.worker)) {
            value.worker = true;
        }

        // Setting the config

        this._config = value;
    }
    
    /**
     * Getter function of the 'data' property.
     * 
     * @return  {Array}     data        export data
     */

    get data() {
        return this._data;
    }

    /**
     * Setter function of the 'data' function property.
     * 
     * @param   {Array}     value       export data
     */

    set data(value) {

        // Throwing error if the data is not an array

        if ($.isEmpty(value) || !$.isArray(value)) {
            throw new Error('data is mandatory and must be an array');
        }

        // Setting the data

        this._data = value;
    }
    
    /**
     * Getter function of the 'refresh' function property.
     * 
     * @return  {Function}  refresh     refresher function
     */

    get refresh() { 
        return this._refresh;
    }

    /**
     * Setter function of the 'refresh' function property.
     * 
     * @param   {Function}  value       refresher function
     */

    set refresh(value) {

        // Throwing error if the refresh is defined and not a function

        if (value && !$.isFunction(value)) {
            throw new Error('refresh must be a function or null/undefined');
        }

        // Setting the refresh

        this._refresh = value;
    }

    /**
     * Does the WebWorker HTML5 feature supported by the browser?
     * 
     * @return  {boolean}   supported   WebWorker is supported or not
     */

    static get isWorkerSupported() {
        return typeof Worker !== 'undefined';
    }

    /**
     * Does the current thread run in a WebWorker context?
     * 
     * @return  {boolean}   worker      current thread is in a WebWorker
     */

    static get isWorker() {
        return typeof importScripts !== 'undefined';
    }

    /**
     * Creates and downloads a MS-Excel file based on the specified
     * configuration and data. The refresh function will be callbacked on
     * every row with the percent parameter. It returns a Promise
     * object.
     * 
     * @param   {Object}    config      export configuration
     * @param   {Array}     data        export data
     * @param   {Function}  refresh     refresher function
     * @return  {Promise}   promise     promise object
     */

    static create(config, data, refresh) {
        return new Excel(config, data, refresh).create();
    }

    /**
     * Creates the MS-Excel file. The refresh function will be callbacked on
     * every row with the percent parameter. It returns a Promise
     * object that contains the Blob as the content of the Excel file.
     * 
     * @return  {Promise}   promise     promise object with the Blob file
     *                                  content
     */

     _generate() {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {
            try {

                // Define the callback refresher that gets the percent

                let refresher = (percent) => {

                    // In case of Worker mode

                    if (Excel.isWorker) {
                        postMessage({
                            cmd:        Excel._COMMAND.PERCENT,
                            value:      percent
                        });
                    } else {

                        // In case of main thread running

                        if (this.refresh) {
                            this.refresh(percent);
                        }                        
                    }
                };

                // Preparing

                let fieldCount = this.config.header.length;
                let rows = this.data.length;
                let cw = [];
                
                // Setting the column widths

                for (let i = 0; i < fieldCount; i++) {
                    cw.push({ wch: this.config.header[i].width || Excel.DEFAULT_CELLWIDTH });
                }

                // Creating the workbook

                let wb = {
                    SheetNames: [this.config.sheet],
                    Sheets:     {}
                };

                // Creating the worksheet

                wb.Sheets[this.config.sheet] = { 
                    '!ref':     'A1:' + XLSX.utils.encode_cell({ c: fieldCount - 1, r: rows }),
                    '!cols':    cw
                };

                // Creating the column headers

                for (let i = 0; i < fieldCount; i++) {
                    wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: i, r: 0 })] = {
                        t:  's',
                        v:  this.config.header[i].name,
                        s:  { font: { bold: true }}
                    };
                }

                // Callbacking the refresher with 0 percent

                refresher(0);
                
                // Exporting the data
                
                for (let i = 0; i < rows; i++) {
                    for (let j = 0; j < fieldCount; j++) {

                        // Getting the cell value

                        let value;

                        if (this.config.header[j].attr) {
                            value = $.getAttr(this.data[i], this.config.header[j].attr);
                        } else {
                            value = this.data[i][j];
                        }
                        
                        // Dumping the string value and setting the format of the cell if it is specified

                        if (this.config.header[j].type === Excel.COLUMN.STRING) {
                            wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })] = {t: 's', v: value };
                            
                            // Formatting

                            if (this.config.header[j].format) {
                                wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })].z = this.config.header[j].format;
                            }

                        // Dumping the date value and setting the format of the cell if it is specified

                        } else if ([Excel.COLUMN.DATE, Excel.COLUMN.DATETIME].includes(this.config.header[j].type)) {

                            // Is it an empty value?

                            if (!value || value === '1900-01-01' || ($.isString(value) && value === '')) {
                                wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })] = {t: 's', v: '' };
                            } else {

                                // Does the date specified as a string?

                                if ($.isString(value)) {
                                    wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })] = {t: 's', v: value };

                                // Does the date specified as a date or number?
        
                                } else if ($.isDate(value) || $.isNumber(value)) {

                                    let format;

                                    // Converting the number to date

                                    if ($.isNumber(value)) {
                                        value = new Date(value);
                                    }

                                    // Is is a date type?

                                    if (this.config.header[j].type === Excel.COLUMN.DATE) {

                                        // Creating UTC date without the time part od the date

                                        value = new Date(Date.UTC(
                                            value.getFullYear(),
                                            value.getMonth(),
                                            value.getDate(),
                                            0, 0, 0
                                        ));

                                        format = this.config.header[j].format || 'yyyy.MM.dd';

                                    // Is is a datetime type?

                                    } else {

                                        // Creating UTC date

                                        value = new Date(Date.UTC(
                                            value.getFullYear(),
                                            value.getMonth(),
                                            value.getDate(),
                                            value.getHours(),
                                            value.getMinutes(),
                                            value.getSeconds()
                                        ));

                                        format = this.config.header[j].format || 'yyyy.MM.dd hh:mm:ss';
                                    }

                                    wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })] = {
                                        t:  'd',
                                        z:  format,
                                        v:  value
                                    };
                                }
                            }

                        // Dumping the number value and setting the format of the cell if it is specified

                        } else {
                            wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })] = {t: 'n', v: value };

                            // Formatting

                            if (this.config.header[j].format) {
                                wb.Sheets[this.config.sheet][XLSX.utils.encode_cell({ c: j, r: i + 1 })].z = this.config.header[j].format;
                            }
                        }
                    }

                    // Callbacking the refresher with the percent
                    
                    refresher(((i + 1) / rows) * 100);
                }

                // Generating the MS-Excel file content

                let content = XLSX.write(wb, { bookType: 'xlsx', bookSST: true, type: 'binary' });
                
                let buffer = new ArrayBuffer(content.length);
                var view = new Uint8Array(buffer);
        
                // Filling the array buffer
            
                for (let i = 0; i !== content.length; ++i) {
                    view[i] = content.charCodeAt(i) & 0xFF;
                }

                // Returns the Blob version of the file content as the parameter
                // of the promise resolve function.

                resolve(new Blob([buffer], { type: 'application/octet-stream' }));
            } catch (e) {

                // Returns the error message

                reject(e.toString());
            }
        });
    }

    /**
     * Creates the MS-Excel file. The refresh function will be callbacked on
     * every row with the percent parameter. It returns a Promise
     * object that contains the Blob as the content of the Excel file.
     * 
     * @return  {Promise}   promise     promise object with the Blob file
     *                                  content
     */

    create() {

        // Saves and downloads the specified file content and callbacks the
        // resolve function.

        let saveContent = (content, resolve) => {
            $.loadScript('file-saver.min.js').then(() => {
                saveAs(
                    content,
                    this.config.filename || (new Date().getTime() + '.xlsx')
                );
                resolve();
            });
        };

        // Does the creation in Worker thread mode?

        if (this.config.worker && Excel.isWorkerSupported) {
           
            // Creating worker if it does not exists yet

            let worker = Excel.WORKERFILE;
            this.worker = this.worker || {};
        
            if(!this.worker[worker]) {
                this.worker[worker] = new Worker(worker);
            }

            // Creating Promise object and returns it

            return new Promise((resolve, reject) => {

                // Sending the creation message to the Worker thread about the
                // file generating.

                this.worker[worker].postMessage(JSON.stringify({ 
                    config: this.config,
                    data: this.data 
                }));
        
                // Worker message handler
                
                this.worker[worker].onmessage = (event) => {
                    let data = event.data;

                    switch (data.cmd) {
                                        
                        // Report created. Saving and downloading the file.
                            
                        case Excel._COMMAND.CREATED:
                            saveContent(data.result, resolve);
                            break;
                            
                        // Percent update request arrived. Callbacking the
                        // refresher if it is specified.
                            
                        case Excel._COMMAND.PERCENT:
                            if (this.refresh) {
                                this.refresh(data.value);
                            }
                            
                            break;

                        default:
                            break;
                    }
                };
            });
        } else {

            // Creating file in the main thread

            return new Promise((resolve, reject) => {

                // Loading scripts that are required for the file generation

                $.loadScript([
                    'xlsx.full.min.js',
                    'blob-stream.js'
                ]).then(() => {

                    // Creating the file and downloading it

                    this._generate().then((result) => {
                        saveContent(result, resolve);
                    });
                });
            });
        }
    }
};



// WEBPACK FOOTER //
// ./src/components/Util/Excel.js